(() => {
  'use strict';
  class ProductPurchase extends HTMLElement {
    connectedCallback() {
      if (this.ready || !window.EYFORM) return;
      this.ready = true;
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = this.querySelector('form');
      this.add = this.querySelector('[data-add]');
      this.status = this.querySelector('[data-purchase-status]');
      this.plan = this.querySelector('[name=selling_plan]');
      this.variantInput = this.querySelector('[name=id]');
      this.sticky = this.querySelector('.sticky-buy');
      this.variantInput.addEventListener('change', () => this.updateVariant());
      this.form.addEventListener('change', event => {
        if (event.target.name === 'purchase' || event.target.name === 'selling_plan') this.updateChoice();
        if (event.target.name === 'gift') this.status.textContent = '';
        if (event.target.name === 'purchase') window.EYFORM.track?.('purchase_option', { purchase: event.target.value });
        if (event.target.name === 'gift') window.EYFORM.track?.('gift_select', { gift_variant_id: Number(event.target.value) });
      });
      this.form.addEventListener('submit', event => { event.preventDefault(); this.submit(); });
      this.querySelector('[data-sticky-add]').addEventListener('click', () => {
        if (!this.querySelector('[name=gift]:checked')) {
          this.querySelector('.gift-picker').scrollIntoView({ block: 'center', behavior: this.motion() });
          this.querySelector('[name=gift]:not(:disabled)')?.focus({ preventScroll: true });
          this.status.textContent = window.EYFORM.strings.gift_required;
        } else this.form.requestSubmit();
      });
      this.updateVariant();
      const desired = new URLSearchParams(location.search).get('purchase');
      if (['subscribe', 'duo'].includes(desired)) {
        const option = this.querySelector(`[name=purchase][value=${desired}]`);
        if (option && !option.disabled) { option.checked = true; this.updateChoice(); }
      }
      this.updateSticky = () => { this.sticky.hidden = this.add.getBoundingClientRect().bottom > 0; };
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.updateSticky(); this.scrollFrame = null; });
      };
      this.observer = new IntersectionObserver(this.updateSticky, { threshold: 0 });
      this.observer.observe(this.add);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    updateVariant() {
      this.variant = this.variants.find(v => Number(v.id) === Number(this.variantInput.value));
      if (!this.variant) { this.add.disabled = true; return; }
      this.querySelector('[data-one-price]').textContent = this.variant.price;
      this.querySelector('[data-duo-saving]').textContent = this.variant.duoSaving;
      this.querySelector('[data-subscribe-card]').hidden = !this.variant.plans.length;
      const sub = this.querySelector('[name=purchase][value=subscribe]');
      sub.disabled = !this.variant.plans.length;
      for (const radio of this.querySelectorAll('[name=purchase]:not([value=subscribe])')) radio.disabled = !!this.variant.requiresPlan;
      if (this.variant.requiresPlan && this.variant.plans.length) sub.checked = true;
      if (!this.variant.plans.length && sub.checked) this.querySelector('[name=purchase][value=one]').checked = true;
      const oldPlan = this.plan.value;
      this.plan.replaceChildren(...this.variant.plans.map(plan => {
        const option = new Option(plan.name, plan.id);
        option.selected = String(plan.id) === oldPlan;
        return option;
      }));
      this.add.disabled = !this.variant.available || !window.EYFORM.config.giftVariants.some(v => v.available && ['Sleep mask','Blue light glasses'].includes(v.title));
      if (!this.variant.available) this.add.textContent = window.EYFORM.strings.sold_out;
      else this.add.textContent = window.EYFORM.strings.add_cart;
      if (this.variant.available && this.add.disabled) this.status.textContent = window.EYFORM.strings.gift_not_ready;
      else this.status.textContent = '';
      this.updateChoice();
    }
    updateChoice() {
      const choice = this.querySelector('[name=purchase]:checked')?.value || 'one';
      const subscribed = choice === 'subscribe' && this.variant.plans.length > 0;
      this.plan.disabled = !subscribed;
      this.querySelector('[data-plan-wrap]').hidden = !subscribed;
      const allocation = this.variant.plans.find(p => String(p.id) === this.plan.value) || this.variant.plans[0];
      if (allocation) {
        this.querySelector('[data-subscribe-price]').textContent = allocation.price;
        this.querySelector('[data-subscribe-label]').textContent = allocation.label;
      }
      const price = choice === 'duo' ? this.querySelector('[data-duo-price]').textContent : subscribed ? allocation.price : this.variant.price;
      this.querySelector('[data-sticky-price]').textContent = price;
      this.querySelector('[data-sticky-add]').disabled = this.add.disabled;
    }
    async submit() {
      if (this.submitting || !this.form.reportValidity()) return;
      const giftId = this.querySelector('[name=gift]:checked')?.value;
      if (!giftId) { this.status.textContent = window.EYFORM.strings.gift_required; return; }
      const choice = this.querySelector('[name=purchase]:checked')?.value;
      this.submitting = true; this.add.disabled = true;
      this.querySelector('[data-sticky-add]').disabled = true;
      this.status.textContent = window.EYFORM.strings.adding;
      try {
        await window.EYFORM.cart.add({ variantId: this.variantInput.value, quantity: choice === 'duo' ? 2 : 1, giftId, sellingPlan: choice === 'subscribe' ? this.plan.value : null });
        this.status.textContent = window.EYFORM.strings.added;
      } catch (error) { this.status.textContent = error.message || window.EYFORM.strings.cart_error; }
      finally {
        this.submitting = false;
        this.add.disabled = !this.variant.available;
        this.querySelector('[data-sticky-add]').disabled = this.add.disabled;
      }
    }
  }
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('.gallery-track');
      this.slides = [...this.querySelectorAll('.gallery-slide')];
      this.addEventListener('click', event => {
        const control = event.target.closest('[data-slide]');
        if (control) this.select(Number(control.dataset.slide), true);
        const zoom = event.target.closest('[data-zoom]');
        if (zoom) {
          const original = zoom.parentElement.querySelector('img');
          const image = original.cloneNode();
          image.removeAttribute('loading'); image.removeAttribute('fetchpriority');
          this.querySelector('[data-zoom-target]').replaceChildren(image);
          window.EYFORM.dialogs.open(this.querySelector('.zoom-dialog'), zoom);
        }
      });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowRight','ArrowLeft'].includes(event.key)) return;
        event.preventDefault();
        this.select(((this.current || 0) + (event.key === 'ArrowRight' ? 1 : -1) + this.slides.length) % this.slides.length, true);
      });
      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (matchMedia('(min-width:990px)').matches || ticking) return;
        ticking = true;
        requestAnimationFrame(() => { this.select(Math.round(this.track.scrollLeft / this.track.clientWidth), false); ticking = false; });
      }, { passive: true });
      this.select(0, false);
    }
    select(index, move) {
      this.current = index;
      this.querySelectorAll('[data-slide]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.slide) === index)));
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        if (i !== index) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      if (move && !matchMedia('(min-width:990px)').matches) this.track.scrollTo({ left: index * this.track.clientWidth, behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth' });
    }
  }
  if (!customElements.get('product-purchase')) customElements.define('product-purchase', ProductPurchase);
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
})();
